import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiRequest, withApi } from "../utils/api";

// El POST de /games devuelve associatedIP como id; se completa con la IP cargada para la tabla.
const withIpData = (game, getState) => {
    if (!game || typeof game.associatedIP !== "string") return game;
    const ip = (getState()?.ips?.ips || []).find((item) => item._id === game.associatedIP);
    return ip ? { ...game, associatedIP: ip } : game;
};

// Todos los juegos, incluidos los ocultos (rol games).
export const fetchGames = createAsyncThunk(
    "games/fetchGames",
    withApi(async (_, { token }) => {
        const data = await apiRequest("/games", { token });
        return Array.isArray(data) ? data : [];
    })
);

// `updates` debe cumplir UpdateGameDto (PATCH parcial).
export const updateGame = createAsyncThunk(
    "games/updateGame",
    withApi(({ id, updates }, { token }) =>
        apiRequest(`/games/${id}`, { method: "PATCH", body: updates, token })
    )
);

export const createGame = createAsyncThunk(
    "games/createGame",
    withApi(async (newGame, { token, getState }) => {
        const data = await apiRequest("/games", { method: "POST", body: newGame, token });
        return withIpData(data, getState);
    })
);

// Sube una imagen pública (folder: "games" | "ips") y devuelve { url, key }.
export const uploadGameImage = createAsyncThunk(
    "games/uploadGameImage",
    withApi(({ file, folder = "games" }, { token }) => {
        const formData = new FormData();
        formData.append("image", file);
        formData.append("folder", folder);
        return apiRequest("/media/images", { method: "POST", body: formData, token });
    })
);

const gamesSlice = createSlice({
    name: "games",
    initialState: {
        games: [],
        statusGames: "idle",
        error: null,
        // Crear y editar tienen su propio estado para no ocultar la tabla mientras se guarda.
        statusSave: "idle",
        errorSave: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Casos para fetchGames
            .addCase(fetchGames.pending, (state) => {
                state.statusGames = "loading";
            })
            .addCase(fetchGames.fulfilled, (state, action) => {
                state.statusGames = "succeeded";
                state.games = action.payload;
            })
            .addCase(fetchGames.rejected, (state, action) => {
                state.statusGames = "failed";
                state.error = action.payload || action.error.message;
            })
            // Casos para updateGame
            .addCase(updateGame.pending, (state) => {
                state.statusSave = "loading";
                state.errorSave = null;
            })
            .addCase(updateGame.fulfilled, (state, action) => {
                state.statusSave = "succeeded";
                const index = state.games.findIndex(game => game._id === action.payload?._id);
                if (index !== -1) {
                    state.games[index] = action.payload;
                }
            })
            .addCase(updateGame.rejected, (state, action) => {
                state.statusSave = "failed";
                state.errorSave = action.payload;
            })
            // Casos para createGame
            .addCase(createGame.pending, (state) => {
                state.statusSave = "loading";
                state.errorSave = null;
            })
            .addCase(createGame.fulfilled, (state, action) => {
                state.statusSave = "succeeded";
                if (action.payload) {
                    state.games.push(action.payload);
                }
            })
            .addCase(createGame.rejected, (state, action) => {
                state.statusSave = "failed";
                state.errorSave = action.payload;
            });
    },
});

export default gamesSlice.reducer;
